import { useEffect, useRef, useState, type ReactNode } from "react";
import { pictureForChannel } from "./liveModel";
import type { LiveData } from "./liveTypes";
import LivePost from "./LivePost";
import type { PostViewport } from "../components/StatusBanner";

/**
 * Live once every channel is out (Figma 08, 6 Live, "the cards flip to their
 * numbers"): the post's files on one line, a tile per channel with its
 * numbers written under the picture (a channel without an account says Creo
 * did not post there), then the numbers. The phone shows the numbers only.
 */
export default function LiveResults({
  data,
  viewport,
  timeZone,
  numbers,
  onOpenLive,
}: {
  data: LiveData;
  viewport: PostViewport;
  timeZone: string;
  /** The numbers block (LiveNumbers), under the tiles. */
  numbers: ReactNode;
  /** Opens a live post's numbers from its "Live since". */
  onOpenLive?: (channelId: string) => void;
}) {
  const phone = viewport === "phone";
  const row = useRef<HTMLUListElement>(null);
  const [rowWidth, setRowWidth] = useState(0);
  useEffect(() => {
    const node = row.current;
    if (!node) return;
    const observer = new ResizeObserver(([entry]) => setRowWidth(entry.contentRect.width));
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  const count = data.channels.filter((channel) => data.posts.some((post) => post.channelId === channel.id)).length;
  // Equal tiles, 200 to 280 wide, as many to a row as fit: 280s 20 apart when
  // they all fit (Figma 177:65940), else 8 apart and narrower (four 218s, 885:110343).
  const gap = rowWidth >= count * 280 + (count - 1) * 20 ? 20 : 8;
  const columns = Math.max(1, Math.min(count, Math.floor((rowWidth + gap) / (200 + gap))));
  const tileWidth = rowWidth ? Math.min(280, Math.floor((rowWidth - (columns - 1) * gap) / columns)) : 280;
  const files = new Map(data.files.map((file) => [file.id, file]));
  const fileNames = data.files.filter((file) => file.status !== "uploading").map((file) => file.name);

  if (phone) return <>{numbers}</>;

  return (
    <div className="flex flex-col gap-7">
      {fileNames.length ? (
        <p className="text-[12.5px] leading-[17.5px] text-co-muted">
          {[`${fileNames.length} ${fileNames.length === 1 ? "file" : "files"}`, ...fileNames].join(" · ")}
        </p>
      ) : null}
      <ul ref={row} className="!m-0 flex list-none flex-wrap items-start !p-0" style={{ gap }} aria-label="The posts, with their numbers">
        {data.channels.map((channel) => {
          const post = data.posts.find((entry) => entry.channelId === channel.id);
          if (!post) return null;
          const fileId = pictureForChannel(data, channel.id);
          return (
            <li key={channel.id} className="min-w-0" style={{ width: tileWidth }}>
              <LivePost
                channel={channel}
                post={post}
                file={fileId ? (files.get(fileId) ?? null) : null}
                size="results"
                results
                fluid
                timeZone={timeZone}
                emptyHint=""
                onOpenLive={onOpenLive && post.liveSince ? () => onOpenLive(channel.id) : undefined}
              />
            </li>
          );
        })}
      </ul>
      {numbers}
    </div>
  );
}
